import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';

import type { FilteredValue, RepeatableSubField } from '../../../../interfaces/input.interface';
import { InputType } from '../../../../interfaces/input.interface';

type DatePickerOverlay = Record<string, unknown> & {
    hide?: () => void;
    hideOverlay?: () => void;
};

const DEFAULT_CALENDAR_DATE_FORMAT = 'dd/mm/yy';
const TWO_DIGITS = 2;
const FULL_YEAR_DIGITS = 4;
const TWO_DIGIT_YEAR_DIVISOR = 100;
const DATE_PARTS_LENGTH = 3;
const DAY_PART_INDEX = 0;
const MONTH_PART_INDEX = 1;
const YEAR_PART_INDEX = 2;
const FIRST_MONTH = 1;
const LAST_MONTH = 12;
const FIRST_DAY = 1;
const FULL_YEAR_THRESHOLD = 100;
const FULL_YEAR_BASE = 2000;
const CALENDAR_EVENT_VALUE_KEYS = ['value', 'date', 'selectedDate'] as const;

@Component({
    selector: 'app-repeatable-cell',
    standalone: false,
    templateUrl: './repeatable-cell.component.html',
    styleUrls: ['./repeatable-cell.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RepeatableCellComponent {
    @Input({ required: true }) public sf!: RepeatableSubField;
    @Input({ required: true }) public rowGroup!: FormGroup;
    @Input() public rowIndex = 0;
    @Input() public isEditable = true;
    @Input() public dropdownOptions: Array<FilteredValue<unknown>> = [];
    @Input() public onlyText = '';
    @Input() public readOnlyText = '';
    @Input() public numericInputClasses: Record<string, boolean> = {};

    public readonly INPUT = InputType;
    public readonly standaloneNgModelOptions = { standalone: true };

    private readonly cdr = inject(ChangeDetectorRef);

    public errorId(controlName: string): string {
        return `repeatable-cell-error-${this.rowIndex}-${controlName}`;
    }

    public hasVisibleError(controlName: string): boolean {
        const control = this.rowGroup.get(controlName);

        return Boolean(control?.invalid && (control.touched || control.dirty));
    }

    public errorMessage(controlName: string): string {
        const errors = this.rowGroup.get(controlName)?.errors ?? {};

        if (typeof errors['invalidPattern'] === 'string') {
            return errors['invalidPattern'];
        }

        if (Boolean(errors['required'])) {
            return 'Este campo es obligatorio.';
        }

        return 'Revisa el valor introducido.';
    }

    public get calendarDateFormat(): string {
        return this.sf.input.calendarConfiguration?.calendarDateFormat ?? DEFAULT_CALENDAR_DATE_FORMAT;
    }

    /**
     * Returns a stable string value for PrimeNG DatePicker when it is used inside a repeatable row.
     */
    public calendarControlValue(controlName: string): string {
        return this.formatCalendarValue(this.rowGroup.get(controlName)?.value);
    }

    /**
     * Writes DatePicker value changes explicitly into the row FormControl.
     */
    public onCalendarValueChange(value: unknown, controlName: string): void {
        this.writeCalendarControlValue(value, controlName);
    }

    /**
     * Normalizes DatePicker selections inside repeatable rows and closes the overlay manually as a fallback.
     */
    public onCalendarSelect(selectedDate: unknown, controlName: string, datePicker: unknown): void {
        const selectedValue = this.writeCalendarControlValue(selectedDate, controlName);
        const callbackDate = this.toDateValue(selectedDate) ?? this.toDateValue(selectedValue);

        if (callbackDate !== null) {
            this.sf.input.calendarConfiguration?.onSelect?.(callbackDate);
        }

        this.closeDatePicker(datePicker);
    }

    private writeCalendarControlValue(value: unknown, controlName: string): string {
        const formattedValue = this.formatCalendarValue(value);
        const control = this.rowGroup.get(controlName);

        if (control) {
            if (String(control.value ?? '') !== formattedValue) {
                control.setValue(formattedValue, { emitEvent: true });
            }

            control.markAsDirty();
            control.markAsTouched();
            control.updateValueAndValidity({ emitEvent: false });
        }

        this.cdr.markForCheck();

        return formattedValue;
    }

    private formatCalendarValue(value: unknown): string {
        const normalizedValue = this.unwrapCalendarEventValue(value);

        if (normalizedValue === null || normalizedValue === undefined) {
            return '';
        }

        if (typeof normalizedValue === 'string') {
            return normalizedValue.trim();
        }

        if (!(normalizedValue instanceof Date) || Number.isNaN(normalizedValue.getTime())) {
            return '';
        }

        const day = normalizedValue.getDate();
        const month = normalizedValue.getMonth() + 1;
        const fullYear = normalizedValue.getFullYear();
        const twoDigitYear = fullYear % TWO_DIGIT_YEAR_DIVISOR;

        return this.calendarDateFormat
            .replace(/dd/g, this.padNumber(day))
            .replace(/d/g, String(day))
            .replace(/mm/g, this.padNumber(month))
            .replace(/m/g, String(month))
            .replace(/yy/g, String(fullYear).padStart(FULL_YEAR_DIGITS, '0'))
            .replace(/y/g, this.padNumber(twoDigitYear));
    }

    private toDateValue(value: unknown): Date | null {
        const normalizedValue = this.unwrapCalendarEventValue(value);

        if (normalizedValue instanceof Date && !Number.isNaN(normalizedValue.getTime())) {
            return normalizedValue;
        }

        if (typeof normalizedValue !== 'string') {
            return null;
        }

        const parts = normalizedValue
            .trim()
            .split('/')
            .map((part) => Number(part));

        if (parts.length !== DATE_PARTS_LENGTH || parts.some((part) => !Number.isInteger(part))) {
            return null;
        }

        const day = parts[DAY_PART_INDEX] ?? 0;
        const month = parts[MONTH_PART_INDEX] ?? 0;
        const year = (parts[YEAR_PART_INDEX] ?? 0) < FULL_YEAR_THRESHOLD ? 
            FULL_YEAR_BASE + (parts[YEAR_PART_INDEX] ?? 0) : 
            parts[YEAR_PART_INDEX] ?? 0;

        if (day < FIRST_DAY || month < FIRST_MONTH || month > LAST_MONTH) {
            return null;
        }

        const date = new Date(year, month - 1, day);

        return Number.isNaN(date.getTime()) ? null : date;
    }


    private unwrapCalendarEventValue(value: unknown): unknown {
        if (value === null || value === undefined || value instanceof Date || typeof value !== 'object') {
            return value;
        }

        const record = value as Record<string, unknown>;

        for (const key of CALENDAR_EVENT_VALUE_KEYS) {
            const candidate = record[key];

            if (candidate !== null && candidate !== undefined) {
                return candidate;
            }
        }

        return value;
    }

    private padNumber(value: number): string {
        return String(value).padStart(TWO_DIGITS, '0');
    }

    private closeDatePicker(datePicker: unknown): void {
        const overlay = datePicker as DatePickerOverlay;

        window.setTimeout((): void => {
            if (typeof overlay.hideOverlay === 'function') {
                overlay.hideOverlay();
                this.cdr.markForCheck();
                return;
            }

            if (typeof overlay.hide === 'function') {
                overlay.hide();
                this.cdr.markForCheck();
                return;
            }

            overlay['overlayVisible'] = false;
            this.cdr.markForCheck();
        });
    }
}
